// Citas (Blueprint §7, VLT-265) — agenda por servicio sobre la tabla real 0007.
// V1: crear/listar/cambiar estado. Confirmaciones y recordatorios por correo
// llegan con el proveedor transaccional (VLT-266, dependencia del propietario).
import { supabase, hasBackend } from '@/lib/supabase';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export type AppointmentStatus =
  | 'scheduled' | 'confirmed' | 'completed' | 'missed' | 'cancelled' | 'rescheduled';

export interface Appointment {
  id: string;
  clientId: string | null;
  clientName: string;
  startsAt: string;          // ISO
  durationMin: number;
  status: AppointmentStatus;
  location: string | null;   // presencial · video · teléfono
  notes: string | null;
  /** VAU-CIT-002: el cliente quiere que le atienda SIEMPRE la misma persona. */
  staffId: string | null;
  staffName: string | null;
  /** VAU-CIT-002: oficina (metadata.office) — solo aplica a presenciales. */
  office: string | null;
}

export interface NewAppointmentInput {
  clientId: string;
  startsAt: string;
  durationMin: number;
  location?: string;
  notes?: string;
  staffId?: string;
  office?: string;
}

export interface AppointmentsService {
  /** Próximas citas del tenant (staff) o del cliente autenticado (RLS decide). */
  upcoming(): Promise<Appointment[]>;
  create(input: NewAppointmentInput): Promise<boolean>;
  setStatus(id: string, status: AppointmentStatus): Promise<boolean>;
  /** VLT-266: reagenda con constancia — la vieja queda 'rescheduled' y nace
   *  una nueva 'scheduled' (RPC 0032; el cliente dueño también puede). */
  reschedule(id: string, newStartsAt: string): Promise<'ok' | 'no_encontrada' | 'estado_invalido' | 'fecha_pasada' | 'error'>;
}

// ─── Reglas de agenda (VLT-349 · VAU-CIT-004/008/011/012) — puras ──────────

/** VAU-CIT-004: una cita de 30 min con colchón de 15 BLOQUEA hasta +45. */
export function blocksUntil(a: Pick<Appointment, 'startsAt' | 'durationMin'>, bufferMin: number): Date {
  return new Date(new Date(a.startsAt).getTime() + (a.durationMin + bufferMin) * 60_000);
}

export type LateState = 'upcoming' | 'grace' | 'lost';

/** VAU-CIT-008/009: pasada la hora, corre la tolerancia de la firma; agotada,
 *  la cita se da por perdida (y se ofrece reprogramar). Puro: recibe `now`. */
export function lateState(
  a: Pick<Appointment, 'startsAt' | 'status'>,
  now: Date,
  toleranceMin: number,
): LateState {
  if (a.status !== 'scheduled' && a.status !== 'confirmed') return 'upcoming';
  const start = new Date(a.startsAt);
  if (now <= start) return 'upcoming';
  const lateMin = (now.getTime() - start.getTime()) / 60_000;
  return lateMin <= toleranceMin ? 'grace' : 'lost';
}

/** VAU-CIT-012: dos colas de trabajo — presenciales (office) y virtuales
 *  (video/teléfono); los huecos se llenan con los virtuales. Devuelve cada
 *  cola en orden con su posición visible (VAU-CIT-011: lista de espera). */
export function splitQueues(appts: Appointment[]): {
  inPerson: Appointment[];
  virtual: Appointment[];
} {
  const open = appts.filter((a) => a.status === 'scheduled' || a.status === 'confirmed');
  return {
    inPerson: open.filter((a) => a.location === 'office'),
    virtual: open.filter((a) => a.location !== 'office'),
  };
}

/** VLT-266: qué toques de la secuencia de recordatorios están VENCIDOS y sin
 *  enviar. `hoursBefore` viene de los ajustes de la firma (p. ej. [24, 4]);
 *  `sentHours` es lo ya enviado (metadata.reminders.sent). Un toque vence
 *  cuando `now >= inicio − h` y la cita aún no empezó. Pura — el job de
 *  Actions replica esta misma regla. */
export function dueReminderTouches(
  hoursBefore: number[],
  sentHours: number[],
  startsAt: string,
  now: Date,
): number[] {
  const start = new Date(startsAt).getTime();
  if (now.getTime() >= start) return [];
  return hoursBefore
    .filter((h) => Number.isFinite(h) && h > 0)
    .filter((h) => now.getTime() >= start - h * 3600_000)
    .filter((h) => !sentHours.includes(h))
    .sort((a, b) => b - a);
}

/** Agrupa citas por día (YYYY-MM-DD) preservando el orden — pura, testeable. */
export function groupByDay(appts: Appointment[]): { day: string; items: Appointment[] }[] {
  const out: { day: string; items: Appointment[] }[] = [];
  for (const a of appts) {
    const day = a.startsAt.slice(0, 10);
    const last = out[out.length - 1];
    if (last && last.day === day) last.items.push(a);
    else out.push({ day, items: [a] });
  }
  return out;
}

// ─── Implementación real (Supabase, RLS por tenant) ─────────────────────────

interface Row {
  id: string; client_id: string | null; starts_at: string; duration_min: number;
  status: AppointmentStatus; location: string | null; notes: string | null;
  staff_id: string | null; metadata: Record<string, unknown> | null;
  clients: { first_name: string; last_name: string } | null;
  staff: { full_name: string } | null;
}

const rowToAppt = (r: Row): Appointment => ({
  id: r.id,
  clientId: r.client_id,
  clientName: r.clients ? `${r.clients.first_name} ${r.clients.last_name}`.trim() : '—',
  startsAt: r.starts_at,
  durationMin: r.duration_min,
  status: r.status,
  location: r.location,
  notes: r.notes,
  staffId: r.staff_id,
  staffName: r.staff?.full_name ?? null,
  office: typeof r.metadata?.office === 'string' ? r.metadata.office : null,
});

const realAppointments: AppointmentsService = {
  async upcoming() {
    const { data, error } = await supabase
      .from('appointments')
      .select('id, client_id, starts_at, duration_min, status, location, notes, staff_id, metadata, clients(first_name, last_name), staff:profiles!staff_id(full_name)')
      // VLT-266: la ventana mira 72h hacia atrás para que una cita PERDIDA
      // reciente aparezca y el cliente pueda reprogramarla desde el enlace.
      .gte('starts_at', new Date(Date.now() - 72 * 3600_000).toISOString())
      .neq('status', 'cancelled')
      .order('starts_at', { ascending: true })
      .limit(100);
    if (error) loadFailed('appointments.upcoming', error);
    return (data as unknown as Row[]).map(rowToAppt);
  },

  async create({ clientId, startsAt, durationMin, location, notes, staffId, office }) {
    const { data: auth } = await sessionUser();
    if (!auth.user) return false;
    const { data: prof } = await supabase.from('profiles').select('organization_id').eq('id', auth.user.id).maybeSingle();
    const orgId = (prof as { organization_id: string } | null)?.organization_id;
    if (!orgId) return false;
    const { error } = await supabase.from('appointments').insert({
      organization_id: orgId, client_id: clientId,
      // VAU-CIT-002: profesional elegido; sin elección, quien crea la cita.
      staff_id: staffId ?? auth.user.id,
      starts_at: startsAt, duration_min: durationMin,
      location: location ?? null, notes: notes ?? null,
      metadata: office ? { office } : {},
    } as never);
    if (error) console.warn('[VaulTex] appointments.create:', error.message);
    return !error;
  },

  async setStatus(id, status) {
    const { error } = await supabase.from('appointments').update({ status } as never).eq('id', id);
    if (error) console.warn('[VaulTex] appointments.status:', error.message);
    return !error;
  },

  async reschedule(id, newStartsAt) {
    const { data, error } = await supabase.rpc('reschedule_appointment', {
      p_id: id,
      p_starts_at: newStartsAt,
    } as never);
    if (error) {
      console.warn('[VaulTex] appointments.reschedule:', error.message);
      return 'error';
    }
    const r = data as string;
    return r === 'ok' || r === 'no_encontrada' || r === 'estado_invalido' || r === 'fecha_pasada' ? r : 'error';
  },
};

// ─── Implementación demo ────────────────────────────────────────────────────

const iso = (daysFromNow: number, hour: number, min = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  d.setHours(hour, min, 0, 0);
  return d.toISOString();
};

const DEMO: Appointment[] = [
  // a0: presencial ya PASADA hoy (8:00) — enseña tolerancia/perdida (VAU-CIT-008)
  { id: 'a0', clientId: 'c3', clientName: 'Rosa Fernández', startsAt: iso(0, 8), durationMin: 30, status: 'scheduled', location: 'office', notes: null, staffId: 'u2', staffName: 'Pedro Herrera', office: 'Oficina principal' },
  { id: 'a1', clientId: 'c1', clientName: 'María Santos', startsAt: iso(0, 15), durationMin: 30, status: 'confirmed', location: 'video', notes: 'Revisión de documentos 2025', staffId: 'u2', staffName: 'Pedro Herrera', office: null },
  { id: 'a2', clientId: 'c4', clientName: 'Núñez LLC', startsAt: iso(0, 16, 30), durationMin: 40, status: 'scheduled', location: 'office', notes: null, staffId: 'u1', staffName: 'Gabriela Cassimmit', office: 'Oficina principal' },
  { id: 'a3', clientId: 'c2', clientName: 'Andrés Rivera', startsAt: iso(1, 10), durationMin: 30, status: 'scheduled', location: 'video', notes: 'Firma de declaración', staffId: 'u3', staffName: 'Sofía Batista', office: null },
];

const fixtureAppointments: AppointmentsService = {
  upcoming: () => Promise.resolve(JSON.parse(JSON.stringify(DEMO)) as Appointment[]),
  create: (input) => {
    DEMO.push({
      id: `a${DEMO.length + 1}`, clientId: input.clientId, clientName: 'Nueva cita',
      startsAt: input.startsAt, durationMin: input.durationMin, status: 'scheduled',
      location: input.location ?? null, notes: input.notes ?? null,
      staffId: input.staffId ?? 'u1', staffName: input.staffId === 'u2' ? 'Pedro Herrera' : input.staffId === 'u3' ? 'Sofía Batista' : 'Gabriela Cassimmit',
      office: input.office ?? null,
    });
    DEMO.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    return Promise.resolve(true);
  },
  setStatus: (id, status) => {
    const a = DEMO.find((x) => x.id === id);
    if (a) a.status = status;
    return Promise.resolve(true);
  },
  reschedule: (id, newStartsAt) => {
    const a = DEMO.find((x) => x.id === id);
    if (!a) return Promise.resolve('no_encontrada' as const);
    if (a.status !== 'scheduled' && a.status !== 'confirmed' && a.status !== 'missed') {
      return Promise.resolve('estado_invalido' as const);
    }
    if (new Date(newStartsAt) <= new Date()) return Promise.resolve('fecha_pasada' as const);
    a.status = 'rescheduled';
    DEMO.push({ ...a, id: `a${DEMO.length + 10}`, startsAt: newStartsAt, status: 'scheduled' });
    DEMO.sort((x, y) => x.startsAt.localeCompare(y.startsAt));
    return Promise.resolve('ok' as const);
  },
};

export const appointmentsService: AppointmentsService = hasBackend ? realAppointments : fixtureAppointments;
